import { useCheckout } from '../../../hooks/useCheckout';

export default function FooterCTA() {
  const { createCheckoutSession, loading, error } = useCheckout();

  const handlePurchase = () => {
    createCheckoutSession('fitness');
  };

  return (
    <footer
      className="bg-[#111111] border-t border-white/5"
      aria-label="Mason Hale Fitness — Footer"
      itemScope
      itemType="https://schema.org/WPFooter"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">

        {/* Main CTA */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#c8a96e]/10 border border-[#c8a96e]/25 rounded-sm px-4 py-2 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#c8a96e]" />
            <span className="text-[#c8a96e] text-xs font-black tracking-[0.25em] uppercase">Limited Time</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
            Ready to Start?
          </h2>
          <p className="text-gray-400 text-base sm:text-lg mb-8 max-w-lg mx-auto leading-relaxed">
            Get the <strong className="text-white">Over-60 Strength Blueprint</strong> and start building real strength today — one price, instant access.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-sm max-w-md mx-auto">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-red-400 text-lg flex-shrink-0" />
                <div>
                  <div className="text-red-400 text-sm font-semibold mb-1">Payment Error</div>
                  <div className="text-red-300 text-xs">{error}</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
            <button
              onClick={handlePurchase}
              disabled={loading}
              aria-label="Purchase the Over-60 Strength Blueprint — $19.99 one-time"
              className="w-full sm:w-auto px-10 py-4 bg-[#c8a96e] text-black text-sm font-black tracking-[0.15em] uppercase rounded-sm hover:bg-[#d9bc82] active:scale-95 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#c8a96e] disabled:active:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line text-base animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                'Get the Program — $19.99'
              )}
            </button>
            <button
              onClick={() => {
                const el = document.getElementById('programs');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label="View the Over-60 Strength Blueprint program details"
              className="w-full sm:w-auto px-10 py-4 bg-white/5 border border-white/15 text-white text-sm font-black tracking-[0.15em] uppercase rounded-sm hover:bg-white/10 active:scale-95 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              View Program
            </button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-5 text-gray-700 text-xs">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-lock-line text-xs" />
            </span>
            <span>Secure checkout · Instant access · One-time purchase</span>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/5 mb-10" />

        {/* Site links + contact */}
        <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-10 mb-10">

          {/* Brand */}
          <div className="flex flex-col items-center md:items-start gap-3">
            <a href="/" className="flex items-center gap-2 cursor-pointer" aria-label="Mason Hale Fitness homepage">
              <div className="w-7 h-7 flex items-center justify-center bg-[#c8a96e] rounded-sm flex-shrink-0">
                <i className="ri-user-line text-black text-sm" />
              </div>
              <span className="text-white font-black text-sm tracking-[0.22em] uppercase">Mason Hale</span>
            </a>
            <p className="text-gray-600 text-xs leading-relaxed max-w-xs text-center md:text-left">
              Real <strong className="text-gray-500">over-60 fitness transformation</strong>. Built for men who want to get strong, lose weight, and feel better — without getting hurt.
            </p>
          </div>

          {/* Quick links */}
          <nav aria-label="Footer navigation" className="flex flex-col items-center md:items-start gap-2">
            <span className="text-gray-600 text-xs tracking-[0.2em] uppercase font-black mb-1">Quick Links</span>
            {[
              { label: 'The Story', href: '#story' },
              { label: 'The Program', href: '#programs' },
              { label: 'Real Results', href: '#testimonials' },
              { label: 'FAQ', href: '#faq' },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  const id = link.href.replace('#', '');
                  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-gray-500 hover:text-[#c8a96e] text-xs transition-colors duration-200 cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Contact */}
          <div className="flex flex-col items-center md:items-start gap-3">
            <span className="text-gray-600 text-xs tracking-[0.2em] uppercase font-black">Get in Touch</span>
            <a
              href="mailto:masonhalefit@gmail.com"
              aria-label="Email Mason Hale at masonhalefit@gmail.com"
              className="flex items-center gap-3 px-5 py-3 bg-white/4 border border-white/8 rounded-sm hover:border-[#c8a96e]/30 hover:bg-white/6 transition-all duration-200 cursor-pointer group"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line text-[#c8a96e] text-lg group-hover:scale-110 transition-transform" />
              </span>
              <span className="text-white text-sm font-semibold">masonhalefit@gmail.com</span>
            </a>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/5 mb-8" />

        {/* Copyright + legal */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-gray-700 text-xs">
          <span>© {new Date().getFullYear()} Mason Hale. All rights reserved.</span>
          <span className="text-gray-800 text-center">
            Results may vary. Individual outcomes depend on effort, consistency, and starting fitness level.
          </span>
        </div>

      </div>
    </footer>
  );
}
